// src/mocks/mockApi.js
// React 17-compatible, no libs. Flip on/off with window.__USE_MOCKS__ = true|false
// Mocks the endpoints used by EliteKanbanBoardV2:
// - GET  /production/orders?page=&pageSize=
// - PATCH/production/steps/:id/status
// - PATCH/production/steps/:id/rank
// - PUT  /production/steps/:id/processes
// - PUT  /production/steps/:id/bom
// - GET  /production/steps/:id/messages
// - POST /production/steps/:id/messages

(function () {
  const CANADIAN_FMT = (d) =>
    d ? new Date(d).toISOString().slice(0, 10) : null;

  // --- Simple seeded RNG for reproducible fake data
  function rng(seed = 42) {
    let s = seed >>> 0;
    return () => (s = (s * 1664525 + 1013904223) >>> 0) / 2 ** 32;
  }

  // --- In-memory DB
  const DB = {
    orders: [], // array
    steps: new Map(), // id -> { id, rank, status, operator, machine, plannedStartISO, plannedFinishISO, processes, bom }
    messages: new Map(), // id -> [{id, author, when, text}]
  };

  // --- Seeders
  function seedOrders({ count = 30, seed = 7 } = {}) {
    const rnd = rng(seed);
    const customers = [
      "Maple Co.",
      "Northern Apparel",
      "Prairie Outfitters",
      "Polar Sports",
      "Aurora Gifts",
    ];
    const products = [
      [
        "TSHIRT-100",
        "Basic Tee",
        "T shirt",
        ["S", "M", "L", "XL"],
        ["Black", "Navy", "White"],
      ],
      ["BPACK-200", "Trail Backpack", "Back pack", [], ["Black", "Olive"]],
      [
        "HOOD-300",
        "Fleece Hoodie",
        "Hoodie",
        ["M", "L", "XL"],
        ["Grey", "Black"],
      ],
      ["CAP-400", "Classic Cap", "Headwear", [], ["Black", "Red", "Blue"]],
    ];
    const decoLib = [
      [45, "Screen Print", "Front Chest"],
      [15, "Embroidery", "Left Chest"],
      [107, "DTF", "Back"],
      [45, "Screen Print", "Full Front"],
    ];
    const today = new Date();
    DB.orders = Array.from({ length: count }).map((_, i) => {
      const [sku, name, cat, sizes, colors] = products[
        (i + 3) % products.length
      ];
      const c = customers[i % customers.length];
      const orderNumber = `EL-${2025}${String(i + 1).padStart(3, "0")}`;
      const inHands = new Date(
        today.getTime() + (Math.floor(rnd() * 20) - 5) * 86400000
      ); // spread +-5..+15 days
      const qty = 25 + Math.floor(rnd() * 125);
      const decoCt = 1 + Math.floor(rnd() * 2);
      const decos = Array.from({ length: decoCt }).map((__, j) => {
        const [chargeId, chargeName, location] = decoLib[
          (i + j) % decoLib.length
        ];
        return {
          decorationId: `${i + 1}-${j + 1}`,
          chargeId,
          chargeName,
          location,
          netCostGoods: +(rnd() * 1.5 + 0.5).toFixed(2),
          retailCostGoods: +(rnd() * 4 + 2).toFixed(2),
          artProofUrl:
            rnd() > 0.5 ? "https://example.com/proofs/sample.pdf" : "",
        };
      });
      return {
        orderNumber,
        customerName: c,
        projectName: `${c} – ${name}`,
        poNumbers: [
          `PO-${1000 + i}`,
          ...(rnd() > 0.8 ? [`PO-${2000 + i}`] : []),
        ],
        inHandsDate: inHands.toISOString(),
        items: [
          {
            itemId: `${i + 1}`,
            productId: sku,
            productName: name,
            quantity: qty,
            sizes,
            colors,
            supplierName: "Elite Supplier",
            category: cat,
            netCostGoods: +(rnd() * 8 + 4).toFixed(2),
            retailCostGoods: +(rnd() * 18 + 12).toFixed(2),
            decorations: decos,
          },
        ],
      };
    });
  }

  // --- Utilities
  function jsonResponse(body, init = 200) {
    return new Response(JSON.stringify(body), {
      status: typeof init === "number" ? init : 200,
      headers: { "Content-Type": "application/json" },
    });
  }

  function parseURL(url) {
    const u = new URL(url, window.location.origin);
    return { pathname: u.pathname, searchParams: u.searchParams };
  }

  function toISOStart(ymd) {
    return ymd && ymd.length === 10 ? `${ymd}T00:00:00` : ymd || "";
  }
  function toISOEnd(ymd) {
    return ymd && ymd.length === 10 ? `${ymd}T23:59:59` : ymd || "";
  }

  // --- Endpoint handlers
  async function handleGetOrders(url) {
    const { searchParams } = parseURL(url);
    const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const pageSize = Math.max(
      1,
      parseInt(searchParams.get("pageSize") || "15", 10)
    );

    const total = DB.orders.length;
    const start = (page - 1) * pageSize;
    const end = Math.min(start + pageSize, total);
    const data = DB.orders.slice(start, end);

    return jsonResponse({ page, pageSize, total, data });
  }

  function ensureStep(id) {
    if (!DB.steps.has(id)) {
      DB.steps.set(id, {
        id,
        status: "Scheduled",
        rank: 1000,
        operator: "",
        machine: "",
        plannedStartISO: "",
        plannedFinishISO: "",
        processes: [],
        bom: [],
      });
    }
    return DB.steps.get(id);
  }

  async function handlePatchStatus(url, init) {
    const match = url.match(/\/production\/steps\/([^/]+)\/status/i);
    if (!match) return jsonResponse({ error: "Bad URL" }, 404);
    const id = decodeURIComponent(match[1]);
    const body = JSON.parse(init.body || "{}");

    const step = ensureStep(id);
    step.status = body.toStatus || step.status;
    if (body.operator !== undefined) step.operator = body.operator;
    if (body.machine !== undefined) step.machine = body.machine;
    if (body.plannedStart) step.plannedStartISO = body.plannedStart;
    if (body.plannedFinish) step.plannedFinishISO = body.plannedFinish;
    // rank is handled separately

    // create a small lifecycle echo response if needed
    return jsonResponse({ ok: true, step });
  }

  async function handlePatchRank(url, init) {
    const match = url.match(/\/production\/steps\/([^/]+)\/rank/i);
    if (!match) return jsonResponse({ error: "Bad URL" }, 404);
    const id = decodeURIComponent(match[1]);
    const { rank } = JSON.parse(init.body || "{}");
    const step = ensureStep(id);
    step.rank = typeof rank === "number" ? rank : step.rank;
    return jsonResponse({ ok: true });
  }

  async function handlePutProcesses(url, init) {
    const match = url.match(/\/production\/steps\/([^/]+)\/processes/i);
    if (!match) return jsonResponse({ error: "Bad URL" }, 404);
    const id = decodeURIComponent(match[1]);
    const arr = JSON.parse(init.body || "[]");
    const step = ensureStep(id);
    step.processes = Array.isArray(arr) ? arr : [];
    return jsonResponse({ ok: true });
  }

  async function handlePutBOM(url, init) {
    const match = url.match(/\/production\/steps\/([^/]+)\/bom/i);
    if (!match) return jsonResponse({ error: "Bad URL" }, 404);
    const id = decodeURIComponent(match[1]);
    const arr = JSON.parse(init.body || "[]");
    const step = ensureStep(id);
    step.bom = Array.isArray(arr) ? arr : [];
    return jsonResponse({ ok: true });
  }

  async function handleGetMessages(url) {
    const match = url.match(/\/production\/steps\/([^/]+)\/messages/i);
    if (!match) return jsonResponse({ error: "Bad URL" }, 404);
    const id = decodeURIComponent(match[1]);
    const list = DB.messages.get(id) || [];
    return jsonResponse({ data: list });
  }

  async function handlePostMessage(url, init) {
    const match = url.match(/\/production\/steps\/([^/]+)\/messages/i);
    if (!match) return jsonResponse({ error: "Bad URL" }, 404);
    const id = decodeURIComponent(match[1]);
    const { text } = JSON.parse(init.body || "{}");
    const list = DB.messages.get(id) || [];
    const msg = {
      id: `m-${Date.now()}`,
      author: "You",
      when: new Date().toISOString(),
      text: String(text || "").slice(0, 2000),
    };
    list.push(msg);
    DB.messages.set(id, list);
    return jsonResponse({ ok: true, message: msg });
  }

  // --- Public API
  function enableMocks({ seedCount = 30, seed = 7 } = {}) {
    if (enableMocks.__active) return;
    seedOrders({ count: seedCount, seed });

    const realFetch = window.fetch.bind(window);
    window.__REAL_FETCH__ = realFetch;

    window.fetch = async (input, init = {}) => {
      const url =
        typeof input === "string" ? input : input.url || String(input);

      // emulate small latency
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      await sleep(120 + Math.random() * 180);

      // ROUTING
      if (url.startsWith("/production/orders")) return handleGetOrders(url);

      if (
        url.match(/\/production\/steps\/[^/]+\/status/i) &&
        (init.method || "GET").toUpperCase() === "PATCH"
      ) {
        return handlePatchStatus(url, init);
      }
      if (
        url.match(/\/production\/steps\/[^/]+\/rank/i) &&
        (init.method || "GET").toUpperCase() === "PATCH"
      ) {
        return handlePatchRank(url, init);
      }
      if (
        url.match(/\/production\/steps\/[^/]+\/processes/i) &&
        (init.method || "GET").toUpperCase() === "PUT"
      ) {
        return handlePutProcesses(url, init);
      }
      if (
        url.match(/\/production\/steps\/[^/]+\/bom/i) &&
        (init.method || "GET").toUpperCase() === "PUT"
      ) {
        return handlePutBOM(url, init);
      }
      if (url.match(/\/production\/steps\/[^/]+\/messages/i)) {
        const method = (init.method || "GET").toUpperCase();
        if (method === "GET") return handleGetMessages(url);
        if (method === "POST") return handlePostMessage(url, init);
      }

      // fall through to real fetch for anything else
      return realFetch(input, init);
    };

    enableMocks.__active = true;
    console.log("[mockApi] enabled with", DB.orders.length, "orders");
  }

  function disableMocks() {
    if (!enableMocks.__active) return;
    if (window.__REAL_FETCH__) window.fetch = window.__REAL_FETCH__;
    enableMocks.__active = false;
    console.log("[mockApi] disabled");
  }

  // expose on window for easy control in dev tools
  window.MockApi = {
    enableMocks,
    disableMocks,
    DB,
    seedOrders,
    CANADIAN_FMT,
    toISOStart,
    toISOEnd,
  };
})();
    